import { useState, useEffect } from 'react';
import { Card, Btn, Pagination, usePagination } from '../../components/UI';
import { useToast } from '../../components/Toast';
import { dosirAPI } from '../../utils/api';

const th = { padding:'10px 14px',textAlign:'left',fontSize:12,fontWeight:700,color:'#64748B',borderBottom:'2px solid #E2E8F0' };
const td = { padding:'10px 14px',fontSize:13,borderBottom:'1px solid #F1F5F9' };

// ── DASHBOARD DOSIR ────────────────────────────────────────
const DashboardTab = () => {
  const [stats, setStats] = useState({ total_personel:0, total_ruangan:0, total_rollpack:0, data_tersimpan:0 });
  const [nrp, setNrp] = useState('');
  const [personel, setPersonel] = useState(null);
  const [searching, setSearching] = useState(false);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    setLoading(true);
    dosirAPI.getDashboard().then(r => setStats(r.data || stats)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!nrp) return toast('NRP wajib diisi', 'error');
    setSearching(true);
    try {
      const r = await dosirAPI.searchPersonel(nrp);
      setPersonel(r.data);
    } catch (err) {
      setPersonel(null);
      toast(err.message || 'Personel tidak ditemukan', 'error');
    } finally {
      setSearching(false);
    }
  };

  const statCards = [
    { label:'Total Personel', value:stats.total_personel, color:'#2563EB', bg:'#EFF6FF' },
    { label:'Total Ruangan', value:stats.total_ruangan, color:'#059669', bg:'#ECFDF5' },
    { label:'Total Rollpack', value:stats.total_rollpack, color:'#D97706', bg:'#FFFBEB' },
    { label:'Data Tersimpan', value:stats.data_tersimpan, color:'#7C3AED', bg:'#F5F3FF' },
  ];

  return (
    <>
      <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))',gap:16,marginBottom:20 }}>
        {statCards.map(s => (
          <div key={s.label} style={{ background:s.bg,borderRadius:12,padding:20,border:`1px solid ${s.color}22` }}>
            <div style={{ fontSize:28,fontWeight:800,color:s.color }}>{loading ? '...' : s.value}</div>
            <div style={{ fontSize:13,color:'#6B7280',marginTop:4 }}>{s.label}</div>
          </div>
        ))}
      </div>
      <Card>
        <h3 style={{ margin:'0 0 16px',fontSize:15,fontWeight:700 }}>🔍 Pencarian Personel</h3>
        <form onSubmit={handleSearch}>
          <div style={{ display:'flex',gap:8 }}>
            <input value={nrp} onChange={e => setNrp(e.target.value)} placeholder="Masukkan NRP personel"
              style={{ flex:1,padding:'9px 12px',border:'1.5px solid #E5E7EB',borderRadius:8,fontSize:13,outline:'none',fontFamily:'inherit' }}/>
            <Btn>{searching ? 'Mencari...' : 'Cari'}</Btn>
          </div>
        </form>
        {personel && (
          <div style={{ marginTop:16,padding:12,background:'#ECFDF5',borderRadius:8,border:'1px solid #D1FAE5' }}>
            <div style={{ fontSize:13,fontWeight:600,color:'#047857' }}>✓ Personel Ditemukan</div>
            <div style={{ fontSize:12,color:'#065F46',marginTop:8 }}>
              <div><strong>NRP:</strong> {personel.nrp}</div>
              <div><strong>Nama:</strong> {personel.nama}</div>
              <div><strong>Pangkat:</strong> {personel.pangkat}</div>
              <div><strong>Jabatan:</strong> {personel.jabatan}</div>
            </div>
          </div>
        )}
      </Card>
      <Card>
        <h3 style={{ margin:'0 0 12px',fontSize:15,fontWeight:700 }}>ℹ️ Informasi Dosir</h3>
        <p style={{ color:'#6B7280',fontSize:13,margin:0 }}>
          Dashboard Dosir menampilkan ringkasan data arsip personel TNI AD. Cari personel berdasarkan NRP,
          atau gunakan menu untuk mengelola data lemari, rak, dan rollpack.
        </p>
      </Card>
    </>
  );
};

// ── INPUT DATA LEMARI ──────────────────────────────────────
const LemariTab = () => {
  const [data, setData] = useState([]);
  const [form, setForm] = useState({ nama_ruangan:'', nomor_lemari:'', keterangan:'' });
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const { page, setPage, pageSize, setPageSize, reset, paginate } = usePagination(10);
  useEffect(() => { reset(); }, [data]);

  const load = async () => {
    setLoading(true);
    try { const r = await dosirAPI.getLemari(); setData(r.data || []); }
    catch {} finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nama_ruangan) return toast('Nama Ruangan wajib diisi', 'error');
    try {
      if (editId) { await dosirAPI.updLemari(editId, form); toast('Data berhasil diubah'); setEditId(null); }
      else { await dosirAPI.addLemari(form); toast('Data berhasil ditambah'); }
      setForm({ nama_ruangan:'', nomor_lemari:'', keterangan:'' });
      load();
    } catch(e) { toast(e.message, 'error'); }
  };

  const handleEdit = (row) => {
    setEditId(row.id);
    setForm({ nama_ruangan:row.nama_ruangan, nomor_lemari:row.nomor_lemari||'', keterangan:row.keterangan||'' });
  };

  return (
    <>
      <Card>
        <h3 style={{ margin:'0 0 16px',fontSize:15,fontWeight:700 }}>{editId ? 'Edit' : 'Tambah'} Data Lemari Dosir</h3>
        <form onSubmit={handleSubmit}>
          <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,marginBottom:16 }}>
            <div style={{ display:'flex',flexDirection:'column',gap:4 }}>
              <label style={{ fontSize:12,fontWeight:600,color:'#374151' }}>Nama Ruangan *</label>
              <input value={form.nama_ruangan} onChange={e => setForm(f=>({...f,nama_ruangan:e.target.value}))} placeholder="Contoh: DOSIR PERWIRA"
                style={{ padding:'9px 12px',border:'1.5px solid #E5E7EB',borderRadius:8,fontSize:13,outline:'none',boxSizing:'border-box',fontFamily:'inherit' }}/>
            </div>
            <div style={{ display:'flex',flexDirection:'column',gap:4 }}>
              <label style={{ fontSize:12,fontWeight:600,color:'#374151' }}>Nomor Lemari</label>
              <input value={form.nomor_lemari} onChange={e => setForm(f=>({...f,nomor_lemari:e.target.value}))} placeholder="Nomor lemari"
                style={{ padding:'9px 12px',border:'1.5px solid #E5E7EB',borderRadius:8,fontSize:13,outline:'none',boxSizing:'border-box',fontFamily:'inherit' }}/>
            </div>
          </div>
          <div style={{ display:'flex',flexDirection:'column',gap:4,marginBottom:16 }}>
            <label style={{ fontSize:12,fontWeight:600,color:'#374151' }}>Keterangan</label>
            <input value={form.keterangan} onChange={e => setForm(f=>({...f,keterangan:e.target.value}))} placeholder="Keterangan tambahan"
              style={{ padding:'9px 12px',border:'1.5px solid #E5E7EB',borderRadius:8,fontSize:13,outline:'none',boxSizing:'border-box',fontFamily:'inherit' }}/>
          </div>
          <div style={{ display:'flex',gap:8 }}>
            <Btn>{editId ? 'Update' : 'Tambah'}</Btn>
            {editId && <Btn variant="secondary" onClick={() => { setEditId(null); setForm({ nama_ruangan:'',nomor_lemari:'',keterangan:'' }); }}>Batal</Btn>}
          </div>
        </form>
      </Card>
      <Card>
        <h3 style={{ margin:'0 0 12px',fontSize:15,fontWeight:700 }}>Data Lemari ({data.length})</h3>
        {loading ? <p>Loading...</p> : (
          (() => { const { data: paged, total, totalPages, from, to } = paginate(data); return (<>
          <table style={{ width:'100%',borderCollapse:'collapse' }}>
            <thead><tr style={{ background:'#F8FAFC' }}>
              {['No','Nama Ruangan','Nomor Lemari','Keterangan','Aksi'].map(h => <th key={h} style={th}>{h}</th>)}
            </tr></thead>
            <tbody>
              {paged.length === 0
                ? <tr><td colSpan={5} style={{ ...td,textAlign:'center',color:'#9CA3AF' }}>Belum ada data lemari</td></tr>
                : paged.map((r,i) => (
                  <tr key={r.id}>
                    <td style={td}>{from + i}</td>
                    <td style={td}>{r.nama_ruangan}</td>
                    <td style={td}>{r.nomor_lemari || '-'}</td>
                    <td style={td}>{r.keterangan || '-'}</td>
                    <td style={td}>
                      <div style={{ display:'flex',gap:6 }}>
                        <Btn variant="secondary" onClick={() => handleEdit(r)}>Edit</Btn>
                        <Btn variant="danger" onClick={async() => { try { await dosirAPI.delLemari(r.id); load(); } catch(e){toast(e.message,'error');} }}>Hapus</Btn>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
          <Pagination page={page} totalPages={totalPages} total={total} from={from} to={to} pageSize={pageSize} onPageChange={setPage} onPageSizeChange={setPageSize} />
          </>); })()
        )}
      </Card>
    </>
  );
};

// ── DATA RAK ──────────────────────────────────────────────
const RakTab = () => {
  const [data, setData] = useState([]);
  const [ruangan, setRuangan] = useState([]);
  const [rollpack, setRollpack] = useState([]);
  const [form, setForm] = useState({ ruangan_id:'', rollpack_id:'', nomor_rak:'', kapasitas:'' });
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const pgRak = usePagination(10);
  useEffect(() => { pgRak.reset(); }, [data]);

  const load = async () => {
    setLoading(true);
    try {
      const [r, ru, rp] = await Promise.all([
        dosirAPI.getRak(),
        dosirAPI.getLemari(),
        dosirAPI.getRollpack(),
      ]);
      setData(r.data || []);
      setRuangan(ru.data || []);
      setRollpack(rp.data || []);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.ruangan_id || !form.nomor_rak) return toast('Ruangan dan Nomor Rak wajib diisi', 'error');
    try {
      if (editId) { await dosirAPI.updRak(editId, form); toast('Data berhasil diubah'); setEditId(null); }
      else { await dosirAPI.addRak(form); toast('Data berhasil ditambah'); }
      setForm({ ruangan_id:'', rollpack_id:'', nomor_rak:'', kapasitas:'' });
      load();
    } catch(e) { toast(e.message, 'error'); }
  };

  const handleEdit = (row) => {
    setEditId(row.id);
    setForm({ ruangan_id:row.ruangan_id||'', rollpack_id:row.rollpack_id||'', nomor_rak:row.nomor_rak||'', kapasitas:row.kapasitas||'' });
  };

  return (
    <>
      <Card>
        <h3 style={{ margin:'0 0 16px',fontSize:15,fontWeight:700 }}>{editId ? 'Edit' : 'Tambah'} Data Rak</h3>
        <form onSubmit={handleSubmit}>
          <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,marginBottom:16 }}>
            <div style={{ display:'flex',flexDirection:'column',gap:4 }}>
              <label style={{ fontSize:12,fontWeight:600,color:'#374151' }}>Ruangan *</label>
              <select value={form.ruangan_id} onChange={e => setForm(f=>({...f,ruangan_id:e.target.value}))}
                style={{ padding:'9px 12px',border:'1.5px solid #E5E7EB',borderRadius:8,fontSize:13,outline:'none',boxSizing:'border-box',fontFamily:'inherit' }}>
                <option value="">-- Pilih Ruangan --</option>
                {ruangan.map(r => <option key={r.id} value={r.id}>{r.nama_ruangan}</option>)}
              </select>
            </div>
            <div style={{ display:'flex',flexDirection:'column',gap:4 }}>
              <label style={{ fontSize:12,fontWeight:600,color:'#374151' }}>Rollpack</label>
              <select value={form.rollpack_id} onChange={e => setForm(f=>({...f,rollpack_id:e.target.value}))}
                style={{ padding:'9px 12px',border:'1.5px solid #E5E7EB',borderRadius:8,fontSize:13,outline:'none',boxSizing:'border-box',fontFamily:'inherit' }}>
                <option value="">-- Pilih Rollpack --</option>
                {rollpack.map(r => <option key={r.id} value={r.id}>{r.nama_rollpack}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,marginBottom:16 }}>
            <div style={{ display:'flex',flexDirection:'column',gap:4 }}>
              <label style={{ fontSize:12,fontWeight:600,color:'#374151' }}>Nomor Rak *</label>
              <input value={form.nomor_rak} onChange={e => setForm(f=>({...f,nomor_rak:e.target.value}))} placeholder="Contoh: 1, 2, 3"
                style={{ padding:'9px 12px',border:'1.5px solid #E5E7EB',borderRadius:8,fontSize:13,outline:'none',boxSizing:'border-box',fontFamily:'inherit' }}/>
            </div>
            <div style={{ display:'flex',flexDirection:'column',gap:4 }}>
              <label style={{ fontSize:12,fontWeight:600,color:'#374151' }}>Kapasitas</label>
              <input type="number" value={form.kapasitas} onChange={e => setForm(f=>({...f,kapasitas:e.target.value}))} placeholder="Jumlah berkas"
                style={{ padding:'9px 12px',border:'1.5px solid #E5E7EB',borderRadius:8,fontSize:13,outline:'none',boxSizing:'border-box',fontFamily:'inherit' }}/>
            </div>
          </div>
          <div style={{ display:'flex',gap:8 }}>
            <Btn>{editId ? 'Update' : 'Tambah'}</Btn>
            {editId && <Btn variant="secondary" onClick={() => { setEditId(null); setForm({ ruangan_id:'',rollpack_id:'',nomor_rak:'',kapasitas:'' }); }}>Batal</Btn>}
          </div>
        </form>
      </Card>
      <Card>
        <h3 style={{ margin:'0 0 12px',fontSize:15,fontWeight:700 }}>Data Rak ({data.length})</h3>
        {loading ? <p>Loading...</p> : (
          (() => { const { data: paged, total, totalPages, from, to } = pgRak.paginate(data); return (<>
          <table style={{ width:'100%',borderCollapse:'collapse' }}>
            <thead><tr style={{ background:'#F8FAFC' }}>
              {['No','Ruangan','Rollpack','No Rak','Kapasitas','Aksi'].map(h => <th key={h} style={th}>{h}</th>)}
            </tr></thead>
            <tbody>
              {paged.length === 0
                ? <tr><td colSpan={6} style={{ ...td,textAlign:'center',color:'#9CA3AF' }}>Belum ada data rak</td></tr>
                : paged.map((r,i) => (
                  <tr key={r.id}>
                    <td style={td}>{from + i}</td>
                    <td style={td}>{r.nama_ruangan || '-'}</td>
                    <td style={td}>{r.nama_rollpack || '-'}</td>
                    <td style={td}>{r.nomor_rak}</td>
                    <td style={td}>{r.kapasitas || '-'}</td>
                    <td style={td}>
                      <div style={{ display:'flex',gap:6 }}>
                        <Btn variant="secondary" onClick={() => handleEdit(r)}>Edit</Btn>
                        <Btn variant="danger" onClick={async() => { try { await dosirAPI.delRak(r.id); load(); } catch(e){toast(e.message,'error');} }}>Hapus</Btn>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
          <Pagination page={pgRak.page} totalPages={totalPages} total={total} from={from} to={to} pageSize={pgRak.pageSize} onPageChange={pgRak.setPage} onPageSizeChange={pgRak.setPageSize} />
          </>); })()
        )}
      </Card>
    </>
  );
};

// ── MAIN ─────────────────────────────────────────────────
export default function DosirPage() {
  const [tab, setTab] = useState('dashboard');
  const tabs = [
    { id:'dashboard', label:'Dashboard Dosir' },
    { id:'lemari',    label:'Input Data Lemari' },
    { id:'rak',       label:'Input Data Rak' },
  ];
  return (
    <div>
      <div style={{ marginBottom:20 }}>
        <h1 style={{ fontSize:22,fontWeight:700,color:'#111827',margin:0 }}>Dosir</h1>
        <p style={{ color:'#6B7280',fontSize:13,margin:'4px 0 0' }}>Manajemen Arsip Personel TNI AD</p>
      </div>
      <div style={{ display:'flex',gap:0,marginBottom:20,borderBottom:'2px solid #E5E7EB' }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            style={{ padding:'10px 20px',border:'none',background:'none',cursor:'pointer',
              fontSize:13,fontWeight:tab===t.id?700:500,
              color:tab===t.id?'#2563EB':'#6B7280',
              borderBottom:tab===t.id?'2px solid #2563EB':'2px solid transparent',
              marginBottom:-2,fontFamily:'inherit',whiteSpace:'nowrap' }}>
            {t.label}
          </button>
        ))}
      </div>
      {tab === 'dashboard' && <DashboardTab/>}
      {tab === 'lemari'    && <LemariTab/>}
      {tab === 'rak'       && <RakTab/>}
    </div>
  );
}
